import * as fs from 'node:fs/promises'
import * as path from 'node:path'
import { pathToFileURL } from 'node:url'
import { type ResultPromise, execa } from 'execa'
import type { SecurityConfig } from '../config/index.js'
import { type IpcTransport, createTransport } from '../core/ipc.js'
import { createLogger } from '../core/logger.js'
import { buildPermissionFlags, buildSandboxEnv, computeEffectivePermissions } from './sandbox.js'
import type { PawConfig, PawDefinition, PawInstance, PawManifest } from './types.js'

const logger = createLogger('paw-loader')

/** Load an in-process Paw by importing its module */
export async function loadInProcessPaw(
	pawPath: string,
	manifest: PawManifest,
	config: PawConfig,
): Promise<PawInstance> {
	const entryPath = path.resolve(pawPath, manifest.entry)

	logger.info(`Loading in-process Paw "${manifest.name}" from ${entryPath}`)

	// file:// URL, not a bare path. Node's ESM loader accepts an absolute POSIX path as a
	// specifier but rejects a Windows one (`C:\...`) with ERR_UNSUPPORTED_ESM_URL_SCHEME, so
	// this line worked everywhere except Windows — where it took down the only in-process Paw
	// (paw-compact) while every subprocess Paw loaded fine.
	const module = await import(pathToFileURL(entryPath).href)
	const definition: PawDefinition = module.default ?? module

	if (definition.onLoad) {
		await definition.onLoad(config)
	}

	return {
		name: manifest.name,
		manifest,
		config,
		healthy: true,
		transport: 'ipc',
		inProcess: true,
		definition,
	}
}

/** Spawn a subprocess Paw and set up IPC transport */
export async function loadSubprocessPaw(
	pawPath: string,
	manifest: PawManifest,
	config: PawConfig,
	projectRoot?: string,
	security?: SecurityConfig,
	onCrash?: (pawName: string) => void,
): Promise<{ instance: PawInstance; transport: IpcTransport }> {
	const entryPath = path.resolve(pawPath, manifest.entry)
	const transport = manifest.transport ?? 'ipc'
	const permissions = computeEffectivePermissions(manifest, config)
	const env = buildSandboxEnv(permissions)

	// Build filesystem permission flags if sandboxing is enabled
	const permFlags = projectRoot
		? buildPermissionFlags(pawPath, manifest.name, permissions, projectRoot, security)
		: []

	// Pre-create paw data directory so sandboxed paw can write to it
	if (projectRoot) {
		const pawDataDir = path.resolve(
			projectRoot,
			'.openvole',
			'paws',
			manifest.name.replace(/^@openvole\//, ''),
		)
		await fs.mkdir(pawDataDir, { recursive: true })
	}

	logger.info(
		`Spawning subprocess Paw "${manifest.name}" (transport: ${transport}) from ${entryPath}`,
	)
	if (permFlags.length > 0) {
		logger.info(`Filesystem sandbox enabled for "${manifest.name}"`)
		logger.debug(`Sandbox flags for "${manifest.name}": ${permFlags.join(' ')}`)
	}

	const stdioConfig =
		transport === 'ipc'
			? (['pipe', 'pipe', 'pipe', 'ipc'] as const)
			: (['pipe', 'pipe', 'pipe'] as const)

	const child = execa('node', [...permFlags, entryPath], {
		env,
		stdio: stdioConfig,
		reject: false,
		cleanup: true,
	})

	// Forward subprocess output for logging
	child.stdout?.on('data', (data: Buffer) => {
		logger.info(`[${manifest.name}] ${data.toString().trimEnd()}`)
	})
	child.stderr?.on('data', (data: Buffer) => {
		logger.warn(`[${manifest.name}] ${data.toString().trimEnd()}`)
	})

	const ipcTransport = createTransport(
		transport,
		child as unknown as import('node:child_process').ChildProcess,
	)

	const instance: PawInstance = {
		name: manifest.name,
		manifest,
		config,
		healthy: true,
		transport,
		inProcess: false,
		process: {
			kill: () => child.kill(),
			pid: child.pid,
		},
		sendRequest: (method, params) => ipcTransport.request(method, params),
	}

	// Handle subprocess exit
	;(child as ResultPromise)
		.then?.((result) => {
			if (instance.healthy) {
				instance.healthy = false
				logger.error(`Paw "${manifest.name}" exited unexpectedly (code: ${result.exitCode})`)
				onCrash?.(manifest.name)
			}
		})
		.catch?.(() => {
			if (instance.healthy) {
				instance.healthy = false
				onCrash?.(manifest.name)
			}
		})

	return { instance, transport: ipcTransport }
}

/** Send a graceful shutdown signal to a subprocess Paw */
export async function shutdownPaw(instance: PawInstance): Promise<void> {
	if (instance.inProcess) {
		if (instance.definition?.onUnload) {
			await instance.definition.onUnload()
		}
		return
	}

	// Mark as shutting down so the exit handler doesn't log "exited unexpectedly"
	instance.healthy = false

	if (instance.sendRequest) {
		try {
			await Promise.race([
				instance.sendRequest('shutdown'),
				new Promise((resolve) => setTimeout(resolve, 1_000)),
			])
		} catch {
			// Process likely already exited
		}
	}

	instance.process?.kill()
	instance.healthy = false
}
